import React from "react";
import { useNavigate } from "react-router-dom";
import { Logo, GhostButton, ThemeToggle } from "../../design-system";
import { typography, surfaces, borders, brandColors, effects, textColors } from "../../design-system/tokens";
import Footer from "../../app/landing/Footer";
import { APP_DOMAIN } from "../utils/config";
import { logoFontStyle } from "../../design-system/brand/logoFont";

/**
 * AuthLayout - Shared layout component for authentication pages (Login/Register)
 * Provides consistent structure with navbar, clean background, and footer.
 *
 * Optional brand props let portals (e.g. affiliate) reuse the layout with their own colors.
 */
const AuthLayout = ({
  title,
  subtitle,
  children,
  navClassName = `${surfaces.glass} shadow-sm border-b ${borders.glass}`,
  logoIconColor = "#3b82f6",
  logoTextColor = brandColors.gradientText,
  domainClassName = brandColors.gradientText,
  backButtonClassName = `${textColors.tertiary} ${textColors.hover}`,
  themeToggleMoonColor = "text-blue-600 dark:text-blue-400",
  themeToggleSunColor,
  themeToggleMoonHoverFill = "hover:fill-blue-600 dark:hover:fill-blue-400",
  themeToggleClassName,
}) => {
  const navigate = useNavigate();

  return (
    <div className={`min-h-screen ${surfaces.modernApp} flex flex-col transition-colors duration-300`}>
      {/* Top navigation with logo - Glassmorphism style */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${navClassName}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Back arrow */}
            <GhostButton
              onClick={() => navigate("/")}
              variant="neutral"
              className={`p-0 transition-colors ${backButtonClassName}`}
              aria-label="Go back to home"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </GhostButton>
            
            {/* Logo in center */}
            <div className="flex items-center">
              <Logo 
                iconSize="w-8 h-8"
                textSize="text-2xl"
                useCurrentColor={false}
                iconColor={logoIconColor}
                textColor={logoTextColor}
              />
            </div>
            
            {/* Theme toggle */}
            <ThemeToggle
              moonColor={themeToggleMoonColor}
              sunColor={themeToggleSunColor}
              moonHoverFill={themeToggleMoonHoverFill}
              className={themeToggleClassName}
            />
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div className="flex-grow flex flex-col pt-16 relative">
        {/* Subtle background glow effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <div className={effects.backgroundGlowBlue}></div>
          <div className={effects.backgroundGlowSky}></div>
        </div>

        <div className="px-4 py-12 flex-grow flex items-center justify-center relative z-10">
          <div className="max-w-md mx-auto w-full">
            {/* Welcome text */}
            {(title || subtitle) && (
              <div className="text-center mb-8">
                {title && (
                  <h1 className={`${typography.h2} ${textColors.bright} mb-4`}>
                    {title}{" "}
                    <span className={domainClassName} style={logoFontStyle}>
                      {APP_DOMAIN}
                    </span>
                  </h1>
                )}
                {subtitle && (
                  <p className={`${typography.bodyLarge} ${textColors.tertiary}`}>
                    {subtitle}
                  </p>
                )}
              </div>
            )}

            {/* Form container */}
            <div className={`${surfaces.glassCard} border ${borders.glass} rounded-2xl shadow-2xl p-8 transition-colors duration-300`}>
              {children}
            </div>
          </div>
        </div>
      </div>
      
      <div className="relative z-10">
        <Footer />
      </div>
    </div>
  );
};

export default AuthLayout;
